import styled, { keyframes, css } from "styled-components";
import CloseIcon from "src/img/chatBubbleActionIcons/cancel_action.svg";
import { portalColors } from "src/components";

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
`;

export const ChatBoxContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: .75rem;
    padding: 0 0 1rem 0;
`;

export const AttachFileContainer = styled.button`
    width: ${p => p.width || "46px"};
    height: ${p => p.height || "46px"};
    border-radius: ${p => p.radius || "50%"}; 
    border: ${p => p.border || `2px solid ${portalColors.textSoft}`};
    background: ${portalColors.surface};
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    flex: 0 0 auto;
`;

export const AttachContainer = styled.div`
    width: 100%;
    max-width: 130px;
    margin: 0 15px;
    height: auto;
    border: none;
    display: flex;
    justify-content: end;
    align-items: center;
    gap: .40rem;
`;

export const AttachmentFileDisplay = styled.div`
    display: flex;
    align-items: center;
    padding: 12px;
    border: 1px solid ${portalColors.border};
    background: ${portalColors.surface};
    border-radius: 6px;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    margin-bottom: 8px;
`;

export const ImageContainer = styled.div`
    position: relative;
    height: 65px;
    display: flex;
    align-items: center;
    flex: 0 0 auto;
`;

export const CloseButton = styled.div`
    position: absolute;
    top: ${p => p.top || "2px"};
    right: ${p => p.right || "2px"};
    background: var(--calendar-surface, #fff) url(${CloseIcon}) center/20px 20px no-repeat;
    border-radius: 50%;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        width: 20px;
        height: 20px;
        cursor: pointer;
    }
`;

export const PreviewImagesRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap; 
    gap: 16px;
    max-height: 180px;
    padding-right: 4px;
    overflow-x: auto;
    flex: 1 1 auto;
    min-width: 0;

    &::-webkit-scrollbar {
        height: 2px;
    }
    &::-webkit-scrollbar-thumb {
        background: var(--calendar-border-strong, #DBE5ED);
        border-radius: 5px;
    }
    &::-webkit-scrollbar-track {
        background: ${portalColors.surfaceSoft};
    }
`;

export const VideoPreviewWrapper = styled.div`
    position: relative;
    width: 101px;
    height: 65px;
    border-radius: 10px;
    overflow: hidden;
    background: #000;
    box-shadow: 0px 3px 6px #00000029;
`;

export const FileAttachment = styled.div`
    display: inline-flex;
    align-items: center;
    background: ${p => p.background || portalColors.surfaceTertiary};
    border-radius: 10px;
    box-shadow: 0px 3px 3px #00000029;
    font-size: 0.85rem;
    color: ${portalColors.text};
    padding: 0 12px;
    height: ${p => p.height || "50px"};
    max-width: 1000px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;

    @media (max-width: 800px) {
        max-width: 101px;
        font-size: 0.75rem;
    }

    ${({ $uploading }) =>
        $uploading && css`
        animation: ${pulse} 1.5s infinite;
    `}

    ${({ $failed }) =>
        $failed && css`
        opacity: 0.5;
        filter: grayscale(10%) contrast(0.8);
    `}
`;

export const PlayIconOverlay = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${({ width }) => width || "30px"};
    height: ${({ height }) => height || "22px"};
    pointer-events: none;
    background: ${p => p.background || "#B6B6B6;"};
    z-index: 10;
    border-radius: 5px;
`;

export const FileOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1.5rem;
    box-sizing: border-box;
`;

export const FileModalContent = styled.div`
    width: ${({ $isPdf }) => $isPdf ? "min(96rem, 100%)" : "auto"};
    max-width: 100%;
    height: ${({ $isPdf }) => $isPdf ? "min(52rem, 100%)" : "auto"};
    max-height: 100%;
    min-width: 0;
    min-height: 0;
    overflow: visible;
    box-sizing: border-box;
    border-radius: 12px;
    padding: 1.5rem;
    background-color: ${portalColors.surface};
    color: ${portalColors.text};
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
`;

export const Title = styled.div`
    font-weight: bold;
    font-size: 1.2rem;
    color: ${portalColors.text};
    overflow-wrap: break-word;
    word-break: break-word;
    white-space: normal;
    margin-bottom: 1rem;
    line-height: 1.4rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid ${portalColors.border};
`;

export const CsvTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-family: Arial, sans-serif;

    td {
        border: 1px solid ${portalColors.border};
        padding: 8px;
        font-size: 0.9rem;
        color: ${portalColors.text};
    }

    tr:nth-child(odd) {
        background-color: ${portalColors.surfaceSoft};
    }

    tr:first-child {
        background-color: ${portalColors.surfaceTertiary};
    }
`;

export const TextPre = styled.pre`
    max-height: 70vh;
    overflow: auto;
    white-space: pre-wrap;
    background: ${portalColors.surfaceSoft};
    padding: 1rem;
    border-radius: 8px;
    font-family: Courier, monospace;
    color: ${portalColors.text};
    font-size: 0.95rem;
    margin-top: 0.5rem;
`;

export const PdfWrapper = styled.div`
    width: 100%;
    min-width: 0;
    min-height: 0;
    flex: 1;
    background: ${portalColors.surface};
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    overflow: hidden;
    margin: 0;

    iframe {
        display: block;
        width: 100%;
        height: 100%;
        min-width: 0;
        border: 0;
    }
`;


export const ModalCloseButtonStyled = styled.button`
    position: absolute;
    top: -12px;
    right: -12px;
    width: 40px;
    height: 40px;
    background: var(--calendar-surface, #fff) url("${CloseIcon}") center/40px 40px no-repeat;
    border: none;
    cursor: pointer;
    z-index: 1000;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
`;

export const CenteredLoader = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
`;
